import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  Calendar,
  Plus,
  Trash2,
  CalendarCheck,
  CalendarX,
  RotateCw,
  Sparkles,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Compute if a schedule is currently active for today.
 * Matches the backend logic in _find_active_schedule().
 */
function isScheduleActiveNow(schedule, today = new Date()) {
  try {
    const sd = new Date(schedule.start_date);
    const ed = new Date(schedule.end_date);
    const t = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    if (schedule.recurring_yearly) {
      const tm = [today.getMonth(), today.getDate()];
      const sm = [sd.getMonth(), sd.getDate()];
      const em = [ed.getMonth(), ed.getDate()];
      const cmp = (a, b) => (a[0] - b[0]) || (a[1] - b[1]);
      if (cmp(sm, em) <= 0) return cmp(sm, tm) <= 0 && cmp(tm, em) <= 0;
      return cmp(tm, sm) >= 0 || cmp(tm, em) <= 0;
    }
    return sd <= t && t <= ed;
  } catch {
    return false;
  }
}

function fmtRange(s) {
  try {
    const sd = new Date(s.start_date);
    const ed = new Date(s.end_date);
    const opts = { day: '2-digit', month: 'short' };
    const full = { day: '2-digit', month: 'short', year: 'numeric' };
    if (s.recurring_yearly) {
      return `${sd.toLocaleDateString('fr-FR', opts)} → ${ed.toLocaleDateString('fr-FR', opts)} (chaque année)`;
    }
    return `${sd.toLocaleDateString('fr-FR', full)} → ${ed.toLocaleDateString('fr-FR', full)}`;
  } catch {
    return `${s.start_date} → ${s.end_date}`;
  }
}

const ThemeScheduler = ({ library = [] }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const [form, setForm] = useState({
    library_id: library[1]?.id || '',
    start_date: '',
    end_date: '',
    recurring_yearly: true,
    priority: 0,
  });

  const token = localStorage.getItem('dermascan_token');
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => {
    axios
      .get(`${API_URL}/settings/theme-schedules`)
      .then((res) => setSchedules(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  // Build the batch of 2026-2027 campaigns
  const buildSeedBatch = () => {
    const byId = Object.fromEntries(library.map((l) => [l.id, l]));
    const entries = [];
    const add = (libId, start_date, end_date, opts = {}) => {
      const lib = byId[libId];
      if (!lib) return;
      entries.push({
        name: lib.name,
        emoji: lib.emoji,
        theme: lib.theme,
        start_date,
        end_date,
        recurring_yearly: opts.recurring_yearly || false,
        priority: opts.priority || 0,
        active: true,
      });
    };

    // Yearly recurring (cover every year 2026+)
    add('valentin', '2026-02-10', '2026-02-14', { recurring_yearly: true });
    add('sadanniversaire', '2026-03-22', '2026-03-28', { recurring_yearly: true, priority: 10 });
    add('independance', '2026-08-01', '2026-08-07', { recurring_yearly: true });
    add('noel', '2026-12-15', '2027-01-05', { recurring_yearly: true });

    // Ramadan — dates hégiriennes (varient chaque année) — 2026 + 2027
    add('ramadan', '2026-02-18', '2026-03-19'); // Ramadan 1447 AH
    add('ramadan', '2027-02-08', '2027-03-08'); // Ramadan 1448 AH

    // Pâques — variable each year
    add('paques', '2026-04-01', '2026-04-05'); // Pâques 2026 = 5 avril
    add('paques', '2027-03-24', '2027-03-28'); // Pâques 2027 = 28 mars

    // Fête des mères France/CI (dernier dimanche de mai)
    add('fete-meres', '2026-05-25', '2026-05-31'); // 31 mai 2026
    add('fete-meres', '2027-05-24', '2027-05-30'); // 30 mai 2027

    // Black Friday (dernier vendredi de novembre)
    add('black-friday', '2026-11-23', '2026-12-01'); // 27 nov 2026
    add('black-friday', '2027-11-22', '2027-11-30'); // 26 nov 2027

    return entries;
  };

  const seedAll = async () => {
    const entries = buildSeedBatch();
    if (!entries.length) {
      toast.error('Bibliothèque de thèmes vide');
      return;
    }
    if (
      !window.confirm(
        `Planifier ${entries.length} campagnes pour 2026-2027 ?\n\nLes doublons seront ignorés automatiquement.`
      )
    ) {
      return;
    }
    setSeeding(true);
    try {
      const res = await axios.post(
        `${API_URL}/settings/theme-schedules/seed`,
        { schedules: entries },
        authHeader
      );
      toast.success(`${res.data.created} planification(s) créée(s), ${res.data.skipped} déjà existante(s)`);
      load();
    } catch (e) {
      toast.error('Erreur lors de la planification');
    } finally {
      setSeeding(false);
    }
  };

  const addSchedule = async () => {
    if (!form.library_id || !form.start_date || !form.end_date) {
      toast.error('Choisissez un thème + les 2 dates');
      return;
    }
    const libItem = library.find((l) => l.id === form.library_id);
    if (!libItem) {
      toast.error('Thème introuvable');
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/settings/theme-schedules`,
        {
          name: libItem.name,
          emoji: libItem.emoji,
          theme: libItem.theme,
          start_date: form.start_date,
          end_date: form.end_date,
          recurring_yearly: form.recurring_yearly,
          priority: parseInt(form.priority, 10) || 0,
          active: true,
        },
        authHeader
      );
      toast.success(`Planification "${libItem.name}" ajoutée`);
      setShowForm(false);
      setForm({
        library_id: library[1]?.id || '',
        start_date: '',
        end_date: '',
        recurring_yearly: true,
        priority: 0,
      });
      load();
    } catch (e) {
      toast.error('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await axios.put(
        `${API_URL}/settings/theme-schedules/${s.id}`,
        { active: !s.active },
        authHeader
      );
      load();
    } catch {
      toast.error('Erreur');
    }
  };

  const removeSchedule = async (s) => {
    if (!window.confirm(`Supprimer la planification "${s.name}" ?`)) return;
    try {
      await axios.delete(`${API_URL}/settings/theme-schedules/${s.id}`, authHeader);
      toast.success('Planification supprimée');
      load();
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const currentActive = schedules.find((s) => s.active !== false && isScheduleActiveNow(s));

  return (
    <Card className="border-[#E8DDD4]" data-testid="theme-scheduler">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Badge className="bg-[#5D4E37]/10 text-[#5D4E37] border-0 mb-2">
              <Calendar className="w-3 h-3 mr-1" />
              Planification automatique
            </Badge>
            <p className="text-[#8B7355] text-sm">
              Le site change de thème automatiquement aux dates définies.
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={seedAll}
              disabled={seeding}
              variant="outline"
              className="border-[#D4AF37] text-[#A67C00] hover:bg-[#D4AF37]/10"
              data-testid="seed-campaigns-btn"
              title="Crée en 1 clic : Saint-Valentin, SADANNIVERSAIRE, Ramadan, Pâques, Fête des Mères, Indépendance CI, Black Friday, Noël (2026 + 2027)"
            >
              <Sparkles className={`w-4 h-4 mr-2 ${seeding ? 'animate-spin' : ''}`} />
              {seeding ? 'Planification…' : 'Planifier 2026-2027'}
            </Button>
            <Button
              onClick={() => setShowForm((v) => !v)}
              className="bg-[#C45C26] hover:bg-[#A84B1F] text-white"
              data-testid="add-schedule-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </div>
        </div>

        {currentActive && (
          <div className="bg-gradient-to-r from-[#2E7D32]/10 to-[#C45C26]/10 border border-[#2E7D32]/30 rounded-xl p-3 flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-[#2E7D32]" />
            <div className="flex-1">
              <p className="text-xs text-[#8B7355] uppercase tracking-wider font-semibold">Actif en ce moment</p>
              <p className="font-bold text-[#5D4E37]">
                {currentActive.emoji} {currentActive.name}
              </p>
            </div>
            <Badge className="bg-[#2E7D32] text-white border-0">{fmtRange(currentActive)}</Badge>
          </div>
        )}

        {showForm && (
          <div className="bg-[#FAF6F1] border border-[#E8DDD4] rounded-xl p-4 space-y-3">
            <div>
              <label className="block text-xs font-semibold text-[#5D4E37] mb-1">Thème</label>
              <select
                value={form.library_id}
                onChange={(e) => setForm((f) => ({ ...f, library_id: e.target.value }))}
                className="w-full p-2 border border-[#E8DDD4] rounded-lg bg-white text-[#5D4E37]"
                data-testid="schedule-theme-select"
              >
                {library
                  .filter((l) => l.id !== 'sada')
                  .map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.emoji} {l.name}
                    </option>
                  ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#5D4E37] mb-1">Date de début</label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                  className="border-[#E8DDD4]"
                  data-testid="schedule-start-date"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-[#5D4E37] mb-1">Date de fin</label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                  className="border-[#E8DDD4]"
                  data-testid="schedule-end-date"
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-[#5D4E37] cursor-pointer">
              <input
                type="checkbox"
                checked={form.recurring_yearly}
                onChange={(e) => setForm((f) => ({ ...f, recurring_yearly: e.target.checked }))}
                className="accent-[#C45C26]"
                data-testid="schedule-recurring"
              />
              <RotateCw className="w-4 h-4 text-[#8B7355]" />
              <span>Répéter <strong>chaque année</strong> (l'année de la date est ignorée)</span>
            </label>
            <div className="flex gap-2 pt-2">
              <Button
                onClick={addSchedule}
                disabled={saving}
                className="bg-[#2E7D32] hover:bg-[#1B5E20] text-white flex-1"
                data-testid="schedule-save-btn"
              >
                {saving ? 'Ajout…' : 'Ajouter la planification'}
              </Button>
              <Button
                onClick={() => setShowForm(false)}
                variant="outline"
                className="border-[#8B7355]/40 text-[#8B7355]"
              >
                Annuler
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-[#8B7355] text-sm py-4">Chargement…</p>
        ) : schedules.length === 0 ? (
          <div className="text-center py-6 bg-[#FAF6F1] rounded-xl border border-dashed border-[#E8DDD4]">
            <Calendar className="w-10 h-10 text-[#C45C26] mx-auto mb-2" />
            <p className="text-[#8B7355] text-sm">Aucune planification pour le moment</p>
            <p className="text-[#8B7355] text-xs mt-1">
              Cliquez sur <strong>Ajouter</strong> pour programmer un thème (ex: Noël du 15 déc au 5 janv)
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-[#E8DDD4]">
            {schedules.map((s) => {
              const active = isScheduleActiveNow(s);
              return (
                <li
                  key={s.id}
                  className={`py-3 flex items-center gap-3 ${s.active === false ? 'opacity-50' : ''}`}
                  data-testid={`schedule-item-${s.id}`}
                >
                  <span className="text-2xl">{s.emoji || '🎨'}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#5D4E37] truncate">
                      {s.name}
                      {active && s.active !== false && (
                        <Badge className="ml-2 bg-[#2E7D32] text-white border-0 text-[10px]">
                          Actif maintenant
                        </Badge>
                      )}
                      {s.recurring_yearly && (
                        <Badge className="ml-2 bg-[#C45C26]/10 text-[#C45C26] border-0 text-[10px]">
                          <RotateCw className="w-2.5 h-2.5 mr-1" />
                          Annuel
                        </Badge>
                      )}
                    </p>
                    <p className="text-xs text-[#8B7355]">{fmtRange(s)}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button
                      onClick={() => toggleActive(s)}
                      title={s.active === false ? 'Activer' : 'Désactiver'}
                      className={`p-2 rounded-lg transition-colors ${
                        s.active === false
                          ? 'bg-[#8B7355]/10 text-[#8B7355] hover:bg-[#8B7355]/20'
                          : 'bg-[#2E7D32]/10 text-[#2E7D32] hover:bg-[#2E7D32]/20'
                      }`}
                    >
                      {s.active === false ? <CalendarX className="w-4 h-4" /> : <CalendarCheck className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => removeSchedule(s)}
                      className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

export default ThemeScheduler;
